import axios, { AxiosError, AxiosResponse } from "axios";
import { showMessage } from "../utils/partialUtils";
import { NotificationAppearance } from "./djangoToastSlice";

declare module "axios" {
  export interface AxiosRequestConfig {
    // Skip the global error toast for callers that handle/ignore failures themselves.
    suppressErrorToast?: boolean;
  }
}

/*
 * Generic error handler for all errors
 */
export class APIError extends Error {
  response?: AxiosResponse;

  constructor(
    message: string,
    response?: AxiosResponse,
    suppressToast?: boolean
  ) {
    super(message);
    this.response = response;

    if (suppressToast) {
      return;
    }

    if (!response) {
      showMessage(NotificationAppearance.ERROR, message);
      return;
    }

    const status = response.status ?? 0;
    let msg: string;
    let level: NotificationAppearance;

    if (status >= 200 && status < 300) {
      msg = this.responseMessage;
      level = NotificationAppearance.SUCCESS;
    } else if (status >= 400 && status < 500) {
      msg = this.responseMessage;
      level = NotificationAppearance.WARNING;
    } else {
      msg =
        (typeof response.data === "object" && response.data?.message) ||
        "An unexpected error occurred";
      level = NotificationAppearance.ERROR;
    }

    if (response.data?.ignore_error) {
      return;
    }
    showMessage(level, msg);
  }

  get responseMessage(): string {
    const data = this.response?.data;

    if (!data) {
      return this.message;
    }

    if (data?.message) {
      return data.message;
    }

    if (data?.detail) {
      return data.detail; // { detail: "You do not have permission to perform this action." }
    }

    if (typeof data === "string") {
      return data.toString();
    }

    try {
      return JSON.stringify(data);
    } catch {
      return this.message;
    }
  }
}

function errorHandler(error: AxiosError): Promise<APIError> {
  const { config, response } = error;
  const { data, method, url } = config;
  return Promise.reject(
    new APIError(
      `ERROR in backend callback:
          responseURL=${url} 
          method=${method} 
          data=${data} 
          message='${error.message}' 
          status=${response?.statusText} (${response?.status})
          errors=${JSON.stringify(response?.data ?? "").slice(0, 500)}`,
      response,
      config?.suppressErrorToast
    )
  );
}

const api = axios.create();

api.interceptors.response.use(null, errorHandler);

export default api;
